/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable } from '@angular/core';
import { AppConfig } from '../models/app-config.interface';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: AppConfig | null = null;
  private readonly configPath = '/assets/config/app-config.json';

  constructor() {}

  /**
   * Loads the application configuration from the JSON file.
   * This method should be called during app initialization.
   * Uses fetch() instead of HttpClient to avoid HTTP interceptors during bootstrap.
   */
  async loadConfig(): Promise<void> {
    try {
      const response = await fetch(this.configPath);
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }
      this.config = await response.json() as AppConfig;
      console.log(`Configuration loaded for environment: ${this.config.env.name}`);
    } catch (error) {
      console.error('Failed to load application configuration:', error);
      throw new Error('Configuration could not be loaded. Please check the config file.');
    }
  }

  /**
   * Gets the loaded configuration.
   * Throws an error if configuration hasn't been loaded yet.
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Gets a specific configuration section.
   * Useful for accessing nested config properties with type safety.
   */
  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.getConfig()[key];
  }

  /**
   * Checks if the application is running in production mode.
   * Handles both string ("true"/"false") and boolean values.
   */
  isProduction(): boolean {
    const productionValue = this.getConfig().production;
    if (typeof productionValue === 'string') {
      return productionValue.toLowerCase() === 'true';
    }
    return productionValue;
  }

  /**
   * Gets the current environment name.
   */
  getEnvironmentName(): string {
    return this.getConfig().env.name;
  }
}
